import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ExpandIcon, MessageCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FetchTenderByIdQuery } from "@/graphql/__generated__/graphql";
import { cn } from "@/lib/utils";
import DocumentChatComponent from "@/pages/EditTender/DocumentChat/DocumentChatComponent";

interface FloatingChatProps {
  tenderByIdDataContext?: FetchTenderByIdQuery;
}

/**
 * FloatingChat component that displays a chat icon button with a popover
 * containing a link to the help page.
 *
 * @component
 * @example
 * <FloatingChat />
 */
function FloatingChat({ tenderByIdDataContext }: FloatingChatProps) {
  const { t } = useTranslation(["components"]);
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "px-0 group fixed bottom-2 right-2 z-50 h-10 max-w-10 overflow-hidden rounded-full bg-blue-400 text-white shadow-lg transition-all duration-200 hover:bg-blue-300 hover:max-w-56 active:scale-95",
            open && "max-w-56"
          )}
        >
          <span className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center">
            <MessageCircle className="h-7 w-7" />
          </span>
          <span
            aria-hidden="true"
            className={cn(
              "block whitespace-nowrap pl-4 pr-10 text-sm font-medium opacity-0 transition-all duration-200 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0",
              open && "opacity-100 translate-x-0"
            )}
          >
            {t("documentchat.buttons.chat")}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        forceMount
        className="w-[min(92dvw,420px)] p-0 data-[state=closed]:hidden sm:w-[min(92dvw,520px)] md:w-[min(92dvw,600px)]"
        align="end"
        side="top"
      >
        <div className="flex items-center justify-end px-3 pt-3">
          <Link to={`/tender/${tenderByIdDataContext?.fetchTenderById.id}/documentchat`}>
            <Button variant="outline" size="icon">
              <ExpandIcon />
            </Button>
          </Link>
        </div>

        <div className="h-[70dvh] pr-2">
          <DocumentChatComponent tenderByIdDataContext={tenderByIdDataContext} variant="popover" />
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default FloatingChat;
